import { Menu, Transition } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/solid";
import { Fragment, useEffect, useState } from "react";
import { DropdownItemSelectedParams } from "./DropdownItemSelectedParams";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import ScaleOutlinedIcon from "@mui/icons-material/ScaleOutlined";
import LanguageIcon from "@mui/icons-material/Language";
import LocationCityOutlinedIcon from "@mui/icons-material/LocationCityOutlined";
import { useTranslation } from "react-i18next";
import FilterListIcon from "@mui/icons-material/FilterList";

type Props = {
  searchList: string[];
  onItemSelected: (itemSelectedParams: DropdownItemSelectedParams) => void;
  initialSelectedIndex?: number;
  highlightedIndex?: number;
  size?: "sm" | "md" | "lg" | "xl";
  textSize?: number;
  frontLogoType?: "location" | "scale" | "language" | "city" | "filter";
  centeredPopup?: boolean;
  hideCaret?: boolean;
  fixedDisplayElement?: JSX.Element;
  onItemHover?: (itemId: number, event: React.MouseEvent) => void;
  itemIdResolver?: (item: string, index: number) => string;
  showplaceholder?: boolean;
  placeholder?: string;
  containerClass?: string;
  inLineClass?: string;
  className?: string;
  maxPlaceHolderWidth?: string;
  dropdownWidth?: string;
};

const DropDown = ({
  searchList,
  onItemSelected,
  initialSelectedIndex = 0,
  size = "sm",
  textSize = 13,
  frontLogoType,
  centeredPopup = false,
  hideCaret = false,
  highlightedIndex,
  fixedDisplayElement,
  onItemHover,
  itemIdResolver,
  showplaceholder = false,
  placeholder,
  containerClass,
  inLineClass,
  className,
  maxPlaceHolderWidth = "100%",
  dropdownWidth,
}: Props) => {
  const [onItemSelectedProxy, setonItemSelectedProxy] =
    useState<(itemSelectedParams: DropdownItemSelectedParams) => void>();
  const { t } = useTranslation();
  const [selectedOption, setSelectedOption] =
    useState<DropdownItemSelectedParams>(
      searchList.length > 0
        ? {
            selectedItem: searchList[initialSelectedIndex],
            selectedIndex: initialSelectedIndex,
            selectionProcess: "Automatic",
          }
        : {
            selectedItem: "",
            selectedIndex: -1,
            selectionProcess: "Automatic",
          }
    );

  useEffect(() => {
    if (!onItemSelectedProxy && onItemSelected) {
      setonItemSelectedProxy(() => onItemSelected);
    }
  }, [onItemSelected, setonItemSelectedProxy, onItemSelectedProxy]);

  useEffect(() => {
    if (onItemSelectedProxy && selectedOption)
      onItemSelectedProxy(selectedOption);
  }, [selectedOption, onItemSelectedProxy]);

  return (
    <div
      className={`text-right w-full ${className}`}
      style={{
        zIndex: 11,
      }}
    >
      <Menu
        as="div"
        className={`relative inline-block text-left w-full ${
          containerClass ?? ""
        }`}
      >
        <div className="flex items-center text-gray-700 ml-2 hover:text-gray-500">
          {frontLogoType === "location" && (
            <LocationOnOutlinedIcon className="" />
          )}
          {frontLogoType === "scale" && <ScaleOutlinedIcon className="" />}
          {frontLogoType === "language" && (
            <LanguageIcon fontSize="large" className="" />
          )}
          {frontLogoType === "city" && (
            <LocationCityOutlinedIcon className="" />
          )}
          {frontLogoType === "filter" && <FilterListIcon className="" />}
          <Menu.Button
            className={` text-[${textSize}]
             ${
               frontLogoType ? "!pl-1" : ""
             } inline-flex items-center ${className} rounded-md ${
              fixedDisplayElement ? "" : "px-4"
            } py-2 font-medium text-gray-700 hover:text-gray-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-white 
            focus-visible:ring-opacity-75 whitespace-nowrap justify-between bg-white `}
          >
            {fixedDisplayElement ? (
              <>{fixedDisplayElement}</>
            ) : (
              <span
                className={`truncate overflow-hidden whitespace-nowrap text-left ${
                  inLineClass ?? ""
                }`}
                style={{maxWidth:maxPlaceHolderWidth}}
                title={t(selectedOption.selectedItem)}
              >
                {showplaceholder && placeholder && `${t(placeholder)} - `}
                {t(selectedOption.selectedItem)}
              </span>
            )}
            {!hideCaret && (
              <ChevronDownIcon className=" h-5 w-5" aria-hidden="true" />
            )}
          </Menu.Button>
        </div>
        <Transition
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <Menu.Items
            style={{
              transform: centeredPopup ? "translateX(-25%)" : "",
              maxHeight: "40rem",
              width: dropdownWidth??"fit-content",
            }}
            className="absolute mt-4 origin-top-right divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none w-full  z-50 overflow-y-auto "
          >
            <div className="px-1 py-1 w-full">
              {searchList.map((item: string, index: number) => {
                return (
                  <Menu.Item key={item}>
                    {({ active }) => (
                      <button type="button"
                        onClick={() => {
                          setSelectedOption({
                            selectedItem: item,
                            selectedIndex: index,
                            selectionProcess: "Manual",
                          });
                        }}
                        className={`${
                          active ? "bg-yardBlue text-white" : "text-gray-900"
                        }  group flex w-full items-center rounded-md px-2 py-2 text-[${textSize}] ${
                          highlightedIndex == index ? "bg-gray-300" : ""
                        }`}
                        onMouseEnter={(event) => {
                          if (itemIdResolver && onItemHover) {
                            const itemId = itemIdResolver(item, index);
                            onItemHover(Number(itemId), event);
                          }
                        }}
                      >
                        <span className="flex-1 flex-grow whitespace-nowrap text-left">
                          {t(item)}
                        </span>
                      </button>
                    )}
                  </Menu.Item>
                );
              })}
            </div>
          </Menu.Items>
        </Transition>
      </Menu>
    </div>
  );
};

export default DropDown;
